import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { transactionService, Transaction } from "@/services/transactions";
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  Receipt,
  TrendingUp,
  Package,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Skeleton from "react-loading-skeleton";

const TransactionHistory = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        const response = await transactionService.fetchTransactionsByUserId();
        console.log(" transactionService response: ", response);
        setTransactions(response.transactions || []);
      } catch (error) {
        console.error("Failed to fetch transactions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "IDR",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTotalSpent = () => {
    if (!Array.isArray(transactions)) return 0;
    return transactions
      .filter(t => t.status !== "EXPIRED")
      .reduce((total, transaction) => total + transaction.grand_total, 0);
  };

  const getTotalDiscounts = () => {
    if (!Array.isArray(transactions)) return 0;
    return transactions.reduce(
      (total, transaction) => total + transaction.discount,
      0
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-green-100 pt-12">
      {/* Header */}
      <header className="pt-6 pb-4">
        <div className="max-w-5xl mx-auto flex items-center gap-4 px-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/profile")}
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>

          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-tight">
              Transaction History
            </h1>
            <p className="text-muted-foreground text-sm">
              Track your purchases and payments
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-2">
        {/* HIDE */}
        {/* Summary Cards */}
        {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <Card className="rounded-2xl bg-white/80 backdrop-blur shadow-sm">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Spent</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(getTotalSpent())}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl bg-white/80 backdrop-blur shadow-sm">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">You Saved</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(getTotalDiscounts())}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl bg-white/80 backdrop-blur shadow-sm">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Transactions</p>
                <p className="text-2xl font-bold">{transactions.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Receipt className="w-6 h-6 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div> */}

        {/* Transactions List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Recent Transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-xl border bg-white/70 backdrop-blur p-5"
                  >
                    <div className="flex items-start justify-between gap-4">

                      {/* Left */}
                      <div className="space-y-3 w-full">
                        <Skeleton width={220} height={18} />
                        <Skeleton width={180} height={14} />
                      </div>

                      {/* Right */}
                      <div className="flex flex-col items-end gap-2">
                        <Skeleton width={90} height={18} />
                        <Skeleton width={70} height={14} />
                      </div>

                    </div>
                  </div>
                ))}
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <Package className="w-8 h-8 text-primary" />
                </div>

                <h3 className="text-xl font-semibold mb-2">
                  No transactions yet
                </h3>

                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Once you purchase video replays, your transactions will appear here.
                </p>

                <Button size="lg" onClick={() => navigate("/brands")}>
                  Browse Courts
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {transactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="rounded-xl border bg-white/70 backdrop-blur p-5 cursor-pointer hover:opacity-80 hover:shadow-md transition"
                    onClick={() => {
                      setSelectedTransaction(transaction);
                      setOpenDialog(true);
                    }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Left */}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">
                            Transaction #{String(transaction.id).slice(0, 8)}
                          </h3>

                          {/* Desktop badge */}
                          <div className="hidden sm:flex items-center gap-2">
                            {transaction.status === "PAID" || transaction.status === "SETTLED" ? (
                              transaction.grand_total === 0 ? (
                                // Status FREE
                                <Badge className="bg-green-400 text-white">
                                  FREE
                                </Badge>
                              ) :
                                // Status PAID
                                <Badge className="bg-green-500 text-white">
                                  {transaction.status}
                                </Badge>
                            ) : transaction.status === "PENDING" ? (
                              <Badge className="bg-yellow-500 text-black">
                                {transaction.status}
                              </Badge>
                            ) : (
                              <Badge className="bg-red-500 text-white">
                                {transaction.status}
                              </Badge>
                            )}

                            {transaction.voucher && (
                              <Badge variant="secondary">Voucher</Badge>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {formatDate(transaction.created_at)}
                        </div>
                      </div>

                      {/* Right */}
                      <div className="text-right">
                        <div className="flex flex-col items-end gap-1">
                          {transaction.discount > 0 && (
                            <span className="text-sm line-through text-muted-foreground">
                              {formatCurrency(transaction.subtotal)}
                            </span>
                          )}
                          <span className="text-lg font-bold">
                            {formatCurrency(transaction.grand_total)}
                          </span>
                        </div>

                        {transaction.discount > 0 && (
                          <p className="text-sm text-green-600">
                            You saved {formatCurrency(transaction.discount)}
                          </p>
                        )}
                        {/* Mobile badge */}
                        <div className="flex justify-end mt-2 sm:hidden">
                          <div className="flex gap-2">
                            {transaction.status === "PAID" || transaction.status === "SETTLED" ? (
                              <Badge className="bg-green-500 text-white">
                                {transaction.status}
                              </Badge>
                            ) : transaction.status === "PENDING" ? (
                              <Badge className="bg-yellow-500 text-black">
                                {transaction.status}
                              </Badge>
                            ) : (
                              <Badge className="bg-red-500 text-white">
                                {transaction.status}
                              </Badge>
                            )}

                            {transaction.voucher && (
                              <Badge variant="secondary">Voucher</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Dialog Detail Transaction */}
            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
              <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">

                <DialogHeader>
                  <DialogTitle>
                    Transaction #{String(selectedTransaction?.id).slice(0, 8)}
                  </DialogTitle>
                </DialogHeader>

                {selectedTransaction && (
                  <div className="space-y-5 text-sm overflow-y-auto pr-2">

                    {/* Transaction Info */}
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Date</span>
                        <span>{formatDate(selectedTransaction.created_at)}</span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status</span>
                        {selectedTransaction.grand_total === 0 ? (
                          <span>FREE</span>
                        ) : (
                          <span>{selectedTransaction.status}</span>
                        )}
                      </div>
                    </div>

                    {/* Items */}
                    {selectedTransaction.transaction_details?.length > 0 && (
                      <div className="space-y-3 border-t pt-4">
                        <p className="font-semibold">Purchased Clips</p>

                        {selectedTransaction.transaction_details.map((item: any) => (
                          <div
                            key={item.id}
                            className="border rounded-lg p-3 flex items-center justify-between"
                          >
                            <div>
                              <p className="font-medium">{item.merchant_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {item.court_name}
                              </p>
                              <p className="text-sm font-semibold mt-1">
                                {formatCurrency(item.total)}
                              </p>
                            </div>

                            <Button
                              size="sm"
                              onClick={() => {
                                setOpenDialog(false);
                                navigate(`/videos/${item.clip_id}`);
                              }}
                            >
                              Watch
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Pricing */}
                    <div className="space-y-2 border-t pt-4">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span>{formatCurrency(selectedTransaction.subtotal)}</span>
                      </div>

                      {selectedTransaction.discount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Discount</span>
                          <span>-{formatCurrency(selectedTransaction.discount)}</span>
                        </div>
                      )}

                      <div className="flex justify-between font-semibold text-lg border-t pt-3">
                        <span>Total</span>
                        <span>{formatCurrency(selectedTransaction.grand_total)}</span>
                      </div>

                      {selectedTransaction.voucher && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Voucher</span>
                          <span>{selectedTransaction.voucher.code}</span>
                        </div>
                      )}
                    </div>

                  </div>
                )}

              </DialogContent>
            </Dialog>

          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default TransactionHistory;
